import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

const PERMISSION_BASE_URL = `${API_BASE_PATH}/permission/permissions`;
const MODULE_BASE_URL = `${API_BASE_PATH}/module/`;

const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// 🔐 Permission Request (POST)
export interface PermissionRequest {
  model_id: number;
  role_name?: string | null;
  employee_id?: string | null;
  allow_all?: boolean | null;
  read?: boolean | null;
  write?: boolean | null;
  create?: boolean | null;
  delete?: boolean | null;
  export?: boolean | null;
}

// ✏️ Permission Update (PUT)
export interface PermissionUpdate {
  permission_id: number;
  allow_all?: boolean | null;
  read?: boolean | null;
  write?: boolean | null;
  create?: boolean | null;
  delete?: boolean | null;
  export?: boolean | null;
}

// 📤 Response structure
export interface PermissionRecord {
  role_name: string;
  employee_id: string | null;
  allow_all: boolean;
  read: boolean;
  write: boolean;
  create: boolean;
  delete: boolean;
  export: boolean;
  model_id: number;
  permission_id: number;
  role: {
    role_name: string;
    status: string;
  };
  module: {
    name: string;
    Status: string;
  };
}

// 📦 Module response
export interface ModuleRecord {
  name: string;
  Status: string;
  model_id: number;
}

// 🌟 POST - Create permission
export const postPermission = async (
  data: PermissionRequest,
  token: string | null
): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    const response = await axios.post(PERMISSION_BASE_URL, formData, {
      headers: {
        accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`,
      },
    });

    toast.success("Permission created successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// 🔁 PUT - Update permission
export const updatePermission = async (
  data: PermissionUpdate,
  token: string | null
): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (key !== "permission_id" && value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    const response = await axios.put(
      `${PERMISSION_BASE_URL}/${data.permission_id}`,
      formData,
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Permission updated successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// 🗑️ DELETE - Remove permission
export const deletePermission = async (
  permission_id: number,
  token: string | null
): Promise<any> => {
  try {
    const response = await axios.delete(`${PERMISSION_BASE_URL}/${permission_id}`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    toast.success("Permission deleted successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// 📥 GET - Fetch all permissions
export const getAllPermissions = async (
  token: string | null
): Promise<PermissionRecord[]> => {
  if (!token) {
    // ✅ Prevent unauthorized fetch
    return [];
  }
  try {
    const response = await axios.get(PERMISSION_BASE_URL, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 401) {
      console.warn("Not authorized to fetch permissions yet");
      return []; // ✅ Fail gracefully without crashing
    }
    const message = extractErrorMessage(error);
    toast.error(message);
    return []; // ✅ Don’t throw; return safe empty data
  }
};

// 📥 GET - Fetch all modules
export const getAllModules = async (
  token: string | null
): Promise<ModuleRecord[]> => {
  try {
    const response = await axios.get(MODULE_BASE_URL, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};
